import React from 'react';
import { Form, FormActionsAlign, FormHandlers } from '@doveit/bricks';
import { companyFormValidationSchema } from './CompanyForm.schema';
import FormGeoField from '../../../geo/components/form-geo-field/FormGeoField';
import { GEO_FORM_MODEL_DEFAULT_VALUES } from '../../../geo/components/form-geo-field/constants';
import { GeoFormModel } from '../../../geo/components/form-geo-field/types';
import { deepMerge } from '../../../utils/object/object';

export interface CompanyFormModel {
  vatNumber: string,
  name: string,
  address: GeoFormModel,
}

interface WithoutCustomSubmit {
  innerRef?: never,
  submitLabel: string,
}

interface WithCustomSubmit {
  innerRef: React.MutableRefObject<FormHandlers>,
  submitLabel?: never,
}

interface WithDuplicateVatNumberPrevention {
  preventDuplicateVatNumber: true,
  excludedVatNumbers?: string[],
}

interface WithoutDuplicateVatNumberPrevention {
  preventDuplicateVatNumber?: false,
  excludedVatNumbers?: never
}

interface CompanyFormPropsBase extends React.AriaAttributes {
  initialValues?: Partial<CompanyFormModel>,
  hiddenFields?: Partial<Record<keyof CompanyFormModel, boolean>>,
  loading?: boolean,
  disabled?: boolean,
  onSubmit: (values: CompanyFormModel) => void,
}

export type CompanyFormProps = CompanyFormPropsBase & (WithoutCustomSubmit | WithCustomSubmit) & (WithDuplicateVatNumberPrevention | WithoutDuplicateVatNumberPrevention);

export const COMPANY_FORM_DEFAULT_VALUES: CompanyFormModel = {
  vatNumber: '',
  name: '',
  address: GEO_FORM_MODEL_DEFAULT_VALUES,
};

const CompanyForm: React.FC<CompanyFormProps> = ({
  initialValues = {},
  innerRef,
  hiddenFields = {},
  preventDuplicateVatNumber = true,
  excludedVatNumbers = [],
  submitLabel = 'Salva',
  ...rest
}) => {
  const mergedValues: CompanyFormModel = React.useMemo(() => deepMerge(
    COMPANY_FORM_DEFAULT_VALUES,
    initialValues,
  ), [initialValues]);

  return (
    <Form
      initialValues={mergedValues}
      validateOnBlur
      validateOnChange={false}
      validationSchema={companyFormValidationSchema(
        hiddenFields,
        preventDuplicateVatNumber,
        excludedVatNumbers,
      )}
      innerRef={innerRef}
      {...rest}
    >
      {!hiddenFields.vatNumber && (
        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="vatNumber"
            label="P. IVA (Completa)"
            aria-label="Campo per inserire la partita IVA della persona giuridica"
            placeholder="Es. 10461690967"
            required
          />
        </Form.Item>
      )}

      {!hiddenFields.name && (
        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="name"
            label="Ragione sociale"
            aria-label="Campo per inserire la ragione sociale della persona giuridica"
            placeholder="Es. Dove.it S.R.L."
            required
          />
        </Form.Item>
      )}

      {!hiddenFields.address && (
        <Form.Item>
          <FormGeoField
            name="address"
            required
            labels={{
              normalizedAddress: 'Indirizzo sede legale',
            }}
          />
        </Form.Item>
      )}

      {!innerRef && (
        <Form.Actions align={FormActionsAlign.RIGHT}>
          <Form.Submit label={submitLabel} />
        </Form.Actions>
      )}
    </Form>
  );
};

export default CompanyForm;
